import { useEffect, useRef, useState } from 'react';
import { formatDayMonthYear } from '../../../utils/dateFormat';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { Card, CardHeader } from '../../../components/ui/Card';
import { useAccess } from '../../../hooks/useAccess';
import {
  useCancelResearch,
  useResearchReport,
  useResearchRun,
  useResearchRuns,
  useStartResearch,
} from '../../../hooks/useSalesResearch';
import {
  fetchResearchPrintHtml,
  isRunActive,
  type ResearchDelivery,
} from '../../../api/salesResearch';
import { Modal } from '../../../components/ui/Modal';
import { NewResearchForm, RESEARCH_BLURB } from './NewResearchForm';
import { ResearchReportView } from './ResearchReportView';
import { ResearchRunList, companyDisplay } from './ResearchRunList';
import { researchSummary } from './researchSummary';
import { ResearchStatusPill } from './ResearchStatusPill';
import { SendReportDialog } from './SendReportDialog';
import { SourceStatusList } from './SourceStatusList';

/**
 * The tab is a library as much as a launcher: every brief ever run stays here to
 * be re-read, re-sent and re-exported, so which one is open lives in the URL
 * rather than in component state. That makes a brief something you can link a
 * colleague to, keep in a tab, and still be looking at after a reload.
 */
const RUN_PARAM = 'run';

export function ResearchPage() {
  const { isAdmin } = useAccess();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sending, setSending] = useState(false);
  // The library is what the tab is read for; the form is one click away rather than above it.
  const [creating, setCreating] = useState(false);

  const selectedId = searchParams.get(RUN_PARAM);
  const selectRun = (runId: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (runId) next.set(RUN_PARAM, runId);
        else next.delete(RUN_PARAM);
        return next;
      },
      // Replaced rather than pushed: clicking through five briefs should not
      // mean five presses of the back button to leave the tab.
      { replace: true },
    );
  };

  // Opening a brief has to move the page to it. The panel renders below the
  // form and the whole table, so on a full library it lands well past the fold
  // and picking a row looks like nothing happened at all.
  const panelRef = useRef<HTMLElement | null>(null);
  const scrolledTo = useRef<string | null>(null);

  const runs = useResearchRuns();
  const start = useStartResearch();
  const cancel = useCancelResearch();

  const selected = useResearchRun(selectedId);
  const run = selected.data?.run ?? null;
  const report = useResearchReport(selectedId, run?.status === 'ready');

  // Follow a run the moment it starts, so the person who clicked sees progress
  // instead of having to find their run in the list.
  useEffect(() => {
    if (start.data?.runId) {
      selectRun(start.data.runId);
      setCreating(false);
    }
    // `selectRun` is deliberately not a dependency: it only ever reads the URL
    // through the functional updater, so it holds no state to go stale, and
    // listing it would re-run this on every navigation.
  }, [start.data?.runId]);

  const inFlight = runs.data?.runs.find((r) => isRunActive(r.status));

  // With nothing chosen, a run in flight is what the page is about, so it
  // selects itself — otherwise the page shows a disabled form, no progress and,
  // because Cancel lives in the selected-run panel, no way to stop the run that
  // is blocking everything.
  useEffect(() => {
    if (!selectedId && inFlight) selectRun(inFlight.id);
  }, [selectedId, inFlight]);

  // Once per selection, not on every poll: the run refetches while it is
  // working, and scrolling on each tick would fight whoever is reading.
  useEffect(() => {
    if (!selectedId || !panelRef.current) return;
    if (scrolledTo.current === selectedId) return;
    scrolledTo.current = selectedId;
    panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectedId, run?.id]);

  return (
    <div className="space-y-6">
      {start.data?.status === 'already_running' && (
        <p className="rounded-md border border-info/20 bg-info-light px-3 py-1.5 text-body-sm text-info-dark">
          That company was already being researched, so this is the run in progress the run in progress.
        </p>
      )}

      <ResearchStatusPill
        run={run}
        {...(start.error instanceof Error ? { triggerError: start.error } : {})}
      />

      <Card padding="md">
        <CardHeader
          title="Briefs"
          // Non-admins read reports but cannot spend on new ones.
          description={[
            runs.data && runs.data.runs.length > 0
              ? `${researchSummary(runs.data.runs)}${runs.data.truncated ? ' (most recent briefs only)' : ''}`
              : null,
            isAdmin ? null : 'Deep research runs are started by an admin. Every brief that has been run is here.',
          ]
            .filter(Boolean)
            .join('. ') || undefined}
          action={
            isAdmin ? (
              <Button type="button" size="sm" onClick={() => setCreating(true)}>
                New research
              </Button>
            ) : undefined
          }
        />
        {isAdmin && inFlight && !creating && (
          <p className="mb-3 text-body-xs text-text-tertiary">
            A run for {inFlight.companyName} is still going; a new one can start once it finishes or is cancelled.
          </p>
        )}
        <ResearchRunList
          runs={runs.data?.runs ?? []}
          selectedId={selectedId}
          onSelect={selectRun}
          truncated={runs.data?.truncated ?? false}
        />
      </Card>

      {/* A shared link outlives the brief it points at. Rendering nothing would
          make a deleted or mistyped run id look like an empty page. */}
      {selectedId && !run && selected.error instanceof Error && (
        <p className="rounded-md border border-error/20 bg-error-light px-3 py-1.5 text-body-sm text-error-dark">
          Could not open that brief. {selected.error.message}
        </p>
      )}

      {run && (
        <section
          ref={panelRef}
          className="scroll-mt-4 rounded-2xl border border-border-light bg-surface-primary p-6"
        >
          <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-body-md font-semibold">{companyDisplay(run).name}</h3>
              <p className="text-body-xs text-text-tertiary">
                Researched {formatDayMonthYear(run.createdAt)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {run.status === 'ready' && report.data && (
                <>
                  <PrintButton runId={run.id} />
                  {isAdmin && (
                    <Button type="button" size="sm" onClick={() => setSending(true)}>
                      Send to colleagues
                    </Button>
                  )}
                </>
              )}
              {/* Not offered while the run is going: the effect below re-opens
                  an in-flight run the moment nothing is selected, so a Close
                  button there would look broken. Cancel is the way out. */}
              {!isRunActive(run.status) && (
                <button
                  type="button"
                  onClick={() => selectRun(null)}
                  className="rounded-md border border-border px-3 py-1.5 text-body-sm text-text-tertiary hover:bg-surface-secondary"
                >
                  Close
                </button>
              )}
            </div>
          </div>

          {run.status === 'ready' && report.data ? (
            <>
              <DeliveryHistory deliveries={report.data.deliveries ?? []} />
              <ResearchReportView
                run={report.data.run}
                report={report.data.report}
                claims={report.data.claims}
              />
            </>
          ) : run.status === 'ready' ? (
            // The run is finished but its brief is still on the way. Without
            // this the panel falls through to the progress branch and tells
            // someone opening a brief from last month that it is "Working…".
            report.error instanceof Error ? (
              <p className="text-body-sm text-error-dark">
                Could not load this brief. {report.error.message}
              </p>
            ) : (
              <p className="text-body-sm text-text-tertiary">Loading the brief…</p>
            )
          ) : run.status === 'failed' ? (
            <div className="space-y-3">
              <p className="text-body-sm">
                <span className="font-medium">This run failed.</span>{' '}
                {run.errorMessage ?? 'No error message was recorded.'}
              </p>
              {/* Even on failure the source roster is shown: which providers
                  answered before it died is the first thing worth knowing. */}
              <SourceStatusList statuses={run.sourceStatuses} />
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-body-sm text-text-tertiary">
                {run.progressMessage ?? 'Working…'}
              </p>
              <SourceStatusList statuses={run.sourceStatuses} />
              {isAdmin && isRunActive(run.status) && (
                <button
                  type="button"
                  onClick={() => cancel.mutate(run.id)}
                  disabled={cancel.isPending}
                  className="rounded-md border border-border px-3 py-1.5 text-body-sm hover:bg-surface-secondary disabled:opacity-50"
                >
                  {cancel.isPending ? 'Cancelling…' : 'Cancel run'}
                </button>
              )}
              {cancel.error instanceof Error && (
                <p className="text-body-sm text-error-dark">{cancel.error.message}</p>
              )}
            </div>
          )}
        </section>
      )}

      {isAdmin && (
        <Modal isOpen={creating} onClose={() => setCreating(false)} title="New research" description={RESEARCH_BLURB} size="lg">
          <NewResearchForm
            bare
            onSubmit={(data) => start.mutate(data)}
            submitting={start.isPending}
            disabled={Boolean(inFlight)}
            {...(inFlight
              ? {
                  disabledReason: `A run for ${inFlight.companyName} is still going.`,
                  // Reachable without first selecting the run: this is the button
                  // that unblocks the form, so it belongs next to the reason the
                  // form is blocked.
                  onCancelInFlight: () => cancel.mutate(inFlight.id),
                  cancelling: cancel.isPending,
                }
              : {})}
          />
          {start.error instanceof Error && (
            <p className="mt-3 text-body-sm text-error-dark">Could not start the run. {start.error.message}</p>
          )}
        </Modal>
      )}

      {sending && run && (
        <SendReportDialog
          runId={run.id}
          companyName={run.companyName}
          onClose={() => setSending(false)}
        />
      )}
    </div>
  );
}

/**
 * Who this brief has already gone to.
 *
 * Shown on the brief rather than hidden in the send dialog because the question
 * it answers — "has Patrick seen this?" — is asked while reading, and a brief
 * pulled out of the library months later carries no other memory of it.
 *
 * Failed sends stay on the list. A colleague who never received it is the whole
 * reason to look.
 */
function DeliveryHistory({ deliveries }: { deliveries: ResearchDelivery[] }) {
  if (deliveries.length === 0) return null;

  return (
    <div className="mb-4 rounded-md border border-border bg-surface-secondary px-3 py-2 text-body-xs text-text-tertiary">
      <span className="font-medium text-text-secondary">Already sent to</span>{' '}
      {deliveries.map((d, i) => (
        <span key={d.id}>
          {i > 0 && ', '}
          <span className={d.status === 'failed' ? 'text-error-dark' : undefined}>
            {d.recipientName ?? d.recipientEmail}
            {d.status === 'failed' && ' (failed)'}
          </span>{' '}
          <span>{formatDayMonthYear(d.sentAt)}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * Fetches the server-rendered print view, drops it into a hidden iframe and
 * triggers the browser's own print dialog, where the user picks "Save as PDF".
 *
 * The alternative — generating the PDF server-side — needs a chromium binary
 * that the production image does not ship.
 *
 * Two things kept this button on "Preparing…" indefinitely, and both are fixed
 * by never trusting a step to complete:
 *
 *  - the HTML is fetched rather than loaded by pointing the iframe at the URL.
 *    An iframe navigation carries the session cookie but gets none of the
 *    refresh-and-retry every other call gets, so an expired access token loaded
 *    a JSON error body — or nothing — with no way to say so.
 *  - the iframe load is raced against a deadline, and the whole thing resets in
 *    a `finally`. The button is disabled while preparing, so any path that
 *    skipped the reset left the only way to export permanently greyed out.
 */
const PRINT_LOAD_TIMEOUT_MS = 15_000;

function PrintButton({ runId }: { runId: string }) {
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const print = async () => {
    setPreparing(true);
    setError(null);
    let frame: HTMLIFrameElement | null = null;

    try {
      const html = await fetchResearchPrintHtml(runId);

      frame = document.createElement('iframe');
      frame.setAttribute('aria-hidden', 'true');
      // Off-screen rather than zero-sized: Chrome will not open a print dialog
      // for a frame it considers to have no layout.
      frame.style.position = 'fixed';
      frame.style.left = '-10000px';
      frame.style.top = '0';
      frame.style.width = '800px';
      frame.style.height = '600px';
      frame.style.border = '0';
      frame.srcdoc = html;

      const target = frame;
      await withDeadline(
        new Promise<void>((resolve) => {
          target.onload = () => resolve();
          document.body.appendChild(target);
        }),
        PRINT_LOAD_TIMEOUT_MS,
        'The brief did not finish rendering. Try again.',
      );

      frame.contentWindow?.focus();
      frame.contentWindow?.print();

      // Left in the DOM briefly: removing it synchronously cancels the print
      // dialog in Safari and Firefox.
      const printed = frame;
      setTimeout(() => printed.remove(), 60_000);
      frame = null;
    } catch (e) {
      frame?.remove();
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreparing(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {error && <span className="text-body-sm text-error-dark">{error}</span>}
      <button
        type="button"
        onClick={print}
        disabled={preparing}
        className="rounded-md border border-border px-3 py-1.5 text-body-sm hover:bg-surface-secondary disabled:opacity-50"
      >
        {preparing ? 'Preparing…' : 'Export PDF'}
      </button>
    </div>
  );
}

function withDeadline<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error(message)), ms);
    }),
  ]).finally(() => clearTimeout(timer)) as Promise<T>;
}
